import { apiService } from '../../services/api';
import { Workflow, VaultAssetPreview } from './types';

export class WorkflowAPI {
  private static baseURL = '/api/v1/workflows';
  
  /**
   * Create a new workflow
   */
  static async create(workflow: Omit<Workflow, '_id'>): Promise<Workflow> {
    const response = await apiService.post(this.baseURL, workflow);
    return response;
  }
  
  /**
   * Get all workflows
   */
  static async getAll(params?: {
    page?: number;
    limit?: number;
    campaign_id?: string;
  }): Promise<{
    workflows: Workflow[];
    total: number;
    page: number;
    limit: number;
  }> {
    const queryString = params ? '?' + new URLSearchParams(params as any).toString() : '';
    const response = await apiService.get<{
      workflows: Workflow[];
      total: number;
      page: number;
      limit: number;
    }>(this.baseURL + queryString);
    return response;
  }
  
  /**
   * Get workflow by ID
   */
  static async getById(id: string): Promise<Workflow> {
    try {
      const response = await apiService.get(`${this.baseURL}/${id}`);
      return response;
    } catch (err: any) {
      // Some deployments may expose alternative endpoint shapes — try fallback patterns on 404
      console.warn('WorkflowAPI.getById primary endpoint failed, trying fallbacks...', err.message || err);
      try {
        const q = await apiService.get(`${this.baseURL}?id=${id}`);
        // backend might return { workflows: [...] }
        if (q && q.workflows && q.workflows.length > 0) return q.workflows[0];
      } catch (e) {
        // ignore
      }
      try {
        const alt = await apiService.get(`${this.baseURL}/get/${id}`);
        return alt;
      } catch (e) {
        // rethrow original
        throw err;
      }
    }
  }
  
  /**
   * Update workflow
   */
  static async update(
    id: string,
    updates: Partial<Workflow>
  ): Promise<{ message: string }> {
    const response = await apiService.put(`${this.baseURL}/${id}`, updates);
    return response;
  }
  
  /**
   * Delete workflow
   */
  static async delete(id: string): Promise<{ message: string }> {
    const response = await apiService.delete(`${this.baseURL}/${id}`);
    return response;
  }
  
  /**
   * Duplicate workflow
   */
  static async duplicate(
    id: string,
    newName: string
  ): Promise<Workflow> {
    const response = await apiService.post(`${this.baseURL}/${id}/duplicate`, {
      name: newName,
    });
    return response;
  }
  
  /**
   * Execute workflow (convert to tasks)
   */
  static async execute(id: string, payload?: any): Promise<{
    task_ids: string[];
    message: string;
    created_at: string;
  }> {
    const response = await apiService.post(`${this.baseURL}/${id}/execute`, payload || {});
    return response;
  }
  
  /**
   * Activate workflow
   */
  static async activate(id: string): Promise<{ message: string }> {
    const response = await apiService.post(`${this.baseURL}/${id}/activate`, {});
    return response;
  }
  
  /**
   * Deactivate workflow
   */
  static async deactivate(id: string): Promise<{ message: string }> {
    const response = await apiService.post(`${this.baseURL}/${id}/deactivate`, {});
    return response;
  }

  /**
   * Run workflow immediately once (one-shot) without changing its run_type or status.
   * Works whether the workflow is active (recurring) or inactive.
   */
  static async runNow(id: string): Promise<{ message: string; workflow_id: string }> {
    const response = await apiService.post(`${this.baseURL}/${id}/run-now`, {});
    return response;
  }
}

/**
 * Task API for creating tasks from workflow
 */
export class TaskAPI {
  private static baseURL = '/api/v1/tasks';
  
  /**
   * Create a task
   */
  static async create(task: {
    type: string;
    name: string;
    account_id: string;
    priority?: number;
    quick_seed_config?: Record<string, any>;
    schedule?: Record<string, any>;
    notes?: string;
  }): Promise<any> {
    const response = await apiService.post(this.baseURL, task);
    return response;
  }
  
  /**
   * Batch create tasks
   */
  static async batchCreate(
    tasks: Array<{
      type: string;
      name: string;
      account_id: string;
      priority?: number;
      quick_seed_config?: Record<string, any>;
      schedule?: Record<string, any>;
      notes?: string;
    }>
  ): Promise<{ created: number; failed: number }> {
    let created = 0;
    let failed = 0;
    
    for (const task of tasks) {
      try {
        await this.create(task);
        created++;
      } catch (error) {
        console.error('Failed to create task:', error);
        failed++;
      }
    }
    
    return { created, failed };
  }
}

/**
 * Media Asset API – vault library listing for the VaultAssetPicker UI component.
 * Uses the same JWT-auth endpoint as the main media-assets page.
 */
export class MediaAssetAPI {
  private static baseURL = '/api/v1/media-assets';

  /**
   * List media assets with optional filtering.
   * Response shape: { success: true, data: { assets, has_more, next_id } }
   */
  static async listAssets(params?: {
    media_type?: 'image' | 'video';
    goal?: string;
    search?: string;
    page_size?: number;
    after_id?: string;
  }): Promise<{ assets: VaultAssetPreview[]; has_more: boolean; next_id?: string }> {
    const qs = params ? '?' + new URLSearchParams(params as any).toString() : '';
    const response = await apiService.get<{
      success: boolean;
      data: { assets: VaultAssetPreview[]; has_more: boolean; next_id?: string };
    }>(this.baseURL + qs);
    return response.data ?? { assets: [], has_more: false };
  }

  /**
   * Returns the best thumbnail source for a vault asset.
   *
   * Priority:
   *  1. `thumbnail_preview_b64` — inline 128-px JPEG base64 included in the
   *     ListAssets response when available (worker-generated at publish time).
   *     Rendered as a data URI — zero extra HTTP call.
  *  2. Relative URL to GET /api/v1/media-assets/:id/thumbnail
   *     (public endpoint, no JWT required, cached immutable by backend).
   */
  static thumbnailUrl(asset: VaultAssetPreview | string): string {
    if (typeof asset !== 'string' && asset.thumbnail_preview_b64) {
      return `data:image/jpeg;base64,${asset.thumbnail_preview_b64}`;
    }
    const id = typeof asset === 'string' ? asset : asset.id;
    return `/api/v1/media-assets/${id}/thumbnail`;
  }
}

// ─── Seeding Contents API ────────────────────────────────────────────────────

/** One record from the seeding_contents collection (subset of backend SeedingContent model). */
export interface SeedingContentItem {
  _id: string;
  account_id: string;
  folder_id?: string;
  platform: string;
  post_url: string;
  /** First ~150 chars of post body — used to locate pending-approval posts before URL is known */
  content_snippet?: string;
  /** "profile" | "group" — where the post lives */
  post_type?: string;
  /** Raw FB composite ID for fanpage posts: <page_id>_<post_id> */
  fanpage_post_id?: string;
  /** FB page_id owning the fanpage post */
  fanpage_page_id?: string;
  /** Base URL (group or profile) used for content-snippet discovery when post_url is empty */
  target_url?: string;
  /** How many times each seeder should interact with this post; default 1 */
  required_interactions: number;
  /** "worker_post" | "manual" */
  source: string;
  is_active: boolean;
  workflow_id?: string;
  node_id?: string;
  created_at: string;
}

export interface SeedingContentFolderItem {
  _id: string;
  name: string;
  description?: string;
  category?: string;
  is_default: boolean;
  status: string;
  created_at: string;
}

/**
 * Fetches seeding_contents records (post URLs registered by workers or manual input).
 * Used by SeedingContentsManagerPanel to show a selectable list instead of raw text input.
 *
 * Route: GET /api/v1/seeding-contents?account_id=<id>&active_only=true
 */
export class SeedingContentsAPI {
  private static baseURL = '/api/v1/seeding-contents';

  /**
   * Load all active post URLs for a given seeding account.
   * Fetches up to 100 items — sufficient for the picker UI.
   */
  static async listByAccount(accountId: string, activeOnly = true): Promise<SeedingContentItem[]> {
    const qs = new URLSearchParams({
      account_id: accountId,
      active_only: activeOnly ? 'true' : 'false',
      page: '1',
      limit: '100',
    }).toString();
    const response = await apiService.get<{
      success: boolean;
      data: { contents: SeedingContentItem[]; total: number; page: number; limit: number };
    }>(`${this.baseURL}?${qs}`);
    return response.data?.contents ?? [];
  }

  /**
   * Load all seeding contents for the current business user (paginated).
   * Used by seeding contents management UI.
   */
  static async listAll(
    page: number = 1,
    limit: number = 50,
    filters?: { account_id?: string; folder_id?: string; active_only?: boolean },
  ): Promise<{
    contents: SeedingContentItem[];
    total: number;
    page: number;
    limit: number;
  }> {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString(),
    };
    if (filters?.account_id) params.account_id = filters.account_id;
    if (filters?.folder_id) params.folder_id = filters.folder_id;
    if (typeof filters?.active_only === 'boolean') {
      params.active_only = filters.active_only ? 'true' : 'false';
    }
    const qs = new URLSearchParams(params).toString();
    const response = await apiService.get<{
      success: boolean;
      data: { contents: SeedingContentItem[]; total: number; page: number; limit: number };
    }>(`${this.baseURL}?${qs}`);
    return response.data ?? { contents: [], total: 0, page, limit };
  }

  /**
   * Delete a single seeding content item by ID.
   */
  static async deleteItem(id: string): Promise<void> {
    await apiService.delete<{ success: boolean; message: string }>(`${this.baseURL}/${id}`);
  }

  /**
   * Get a single seeding content item details.
   */
  static async getItem(id: string): Promise<SeedingContentItem> {
    const response = await apiService.get<{
      success: boolean;
      data: SeedingContentItem;
    }>(`${this.baseURL}/${id}`);
    return response.data ?? ({} as SeedingContentItem);
  }

  /**
   * Manually create a seeding content item.
   */
  static async create(payload: {
    post_url: string;
    platform: 'facebook' | 'instagram' | 'threads' | 'tiktok' | 'zalo' | 'linkedin';
    account_id?: string;
    post_type?: 'group' | 'profile' | 'fanpage';
    target_url?: string;
    content_snippet?: string;
    folder_id?: string;
  }): Promise<void> {
    await apiService.post(`${this.baseURL}`, { source: 'manual', ...payload });
  }
}

export class SeedingContentFolderAPI {
  private static baseURL = '/api/v1/seeding-content-folders';

  static async listAll(page: number = 1, limit: number = 100): Promise<{
    folders: SeedingContentFolderItem[];
    total: number;
    page: number;
    limit: number;
  }> {
    const qs = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    }).toString();
    const response = await apiService.get<{
      success: boolean;
      data: { folders: SeedingContentFolderItem[]; total: number; page: number; limit: number };
    }>(`${this.baseURL}?${qs}`);
    return response.data ?? { folders: [], total: 0, page, limit };
  }

  static async create(payload: { name: string; description?: string }): Promise<SeedingContentFolderItem> {
    const response = await apiService.post<{
      success: boolean;
      data: SeedingContentFolderItem;
    }>(this.baseURL, payload);
    return response.data as SeedingContentFolderItem;
  }

  static async update(id: string, payload: { name?: string; description?: string; status?: string }): Promise<SeedingContentFolderItem> {
    const response = await apiService.put<{
      success: boolean;
      data: SeedingContentFolderItem;
    }>(`${this.baseURL}/${id}`, payload);
    return response.data as SeedingContentFolderItem;
  }

  static async delete(id: string): Promise<void> {
    await apiService.delete(`${this.baseURL}/${id}`);
  }
}

// ─── AI Workflow API ──────────────────────────────────────────────────────────

export interface AIWorkflowSummary {
  workflow_id: string;
  task_type: string;
  name: string;
  description: string;
  provider: string;
}

/**
 * Fetches the list of active AI workflows from svc-ai-controller (via svc-business-marketing proxy).
 * Used by the AIGenerateAssetPanel to populate the task_type dropdown.
 *
 * Route: GET /api/marketing/workflows/ai-tasks → svc-business-marketing /api/v1/workflows/ai-tasks
 *        → svc-ai-controller GET /api/v1/workflows?worker_type=ai-controller
 */
export class AIWorkflowAPI {
  private static baseURL = '/api/v1/workflows/ai-tasks';

  /**
   * List all active ai-controller workflow definitions.
   * Returns [{workflow_id, task_type, name, description, provider}].
   */
  static async list(): Promise<AIWorkflowSummary[]> {
    const response = await apiService.get<{
      success: boolean;
      workflows: AIWorkflowSummary[];
      count: number;
    }>(this.baseURL);
    return response.workflows ?? [];
  }
}
